/**
 * The API contract, transcribed from the live OpenAPI document.
 *
 * These types are hand-written rather than generated, and that is a deliberate
 * trade: a generated file is always right about shape and always silent about
 * meaning, and the meaning is the part this product keeps getting wrong. Every
 * field that carries a semantic trap is annotated here, at the boundary, where
 * the next person to use it will be looking.
 *
 * The rule this file exists to enforce: **nothing in the UI may invent a field
 * the backend does not send.** If a screen wants a number the API has no name
 * for, the answer is that the product does not know it yet.
 */

// --- enumerations ----------------------------------------------------------
// Wire values, lowercase, exactly as the backend serialises them. None of these
// strings may ever reach the screen - see i18n/terms.ts.

export const SUBJECT_TYPES = [
  "human_head",
  "human_body",
  "object",
  "room",
  "building",
  "scene",
  "human_in_environment",
] as const;
export type SubjectType = (typeof SUBJECT_TYPES)[number];

/**
 * Whether a project may be shown geometry nobody photographed.
 *
 * Asked once, when the project is made. `precise` is the default and the
 * conservative one: missing surface stays missing and the product asks for
 * another photograph rather than inventing one. Under `visual_completion`
 * missing surface may be filled in, and everything filled in is marked as
 * such everywhere it appears - the viewer, a measurement, an export.
 */
export const GEOMETRY_MODES = ["precise", "visual_completion"] as const;
export type GeometryMode = (typeof GEOMETRY_MODES)[number];

/**
 * Where one piece of the delivered surface came from.
 *
 * Ordered most to least trustworthy, and that order is used: a surface is
 * only as verified as its least verified part. Mirrors
 * `domain/geometry_mode.SurfaceProvenance` exactly - a value added there
 * without being added here would render as an unknown class rather than
 * quietly as an observed one.
 */
export const SURFACE_PROVENANCES = [
  "observed",
  "weakly_observed",
  "ai_completed",
  "unverified",
] as const;
export type SurfaceProvenance = (typeof SURFACE_PROVENANCES)[number];

export interface SubjectCapabilities {
  capture: boolean;
  phone_capture: boolean;
  generic_quality: boolean;
  head_analysis: boolean;
  semantic_coverage: boolean;
  reconstruction: boolean;
  live_guidance: boolean;
  dense_future: boolean;
  mesh_future: boolean;
}

/**
 * The shared workflow read model, evaluated by the backend.
 *
 * Nothing here is derived on the client. `domain/workflow.py` runs the rules
 * once - for the list and for a single project through the same aggregate -
 * so Ana Sayfa, Projelerim and Proje Genel Bakış cannot disagree about where
 * a project stands.
 */
export const WORKFLOW_STEP_KEYS = [
  "media",
  "quality",
  "head_analysis",
  "coverage",
  "video_preparation",
  "sparse",
  "final_model",
] as const;
export type WorkflowStepKey = (typeof WORKFLOW_STEP_KEYS)[number];

/**
 * `todo` is *not measured yet* and must never be coloured as a problem;
 * `attention` is measured and needs a decision; `not_applicable` is neutral
 * (a room has no head coverage, which is not a deficiency); `unavailable`
 * means this deployment cannot perform the step at all.
 */
export type WorkflowStepState =
  | "done"
  | "attention"
  | "todo"
  | "not_applicable"
  | "unavailable";

export interface WorkflowStep {
  key: WorkflowStepKey;
  state: WorkflowStepState;
  count: number | null;
  total: number | null;
}

export type WorkflowActionKey =
  | "add_capture"
  | "phone_capture"
  | "upload_media"
  | "run_analysis"
  | "prepare_videos"
  | "measure_coverage"
  | "add_correction_capture"
  | "start_reconstruction"
  | "view_reconstruction"
  | "view_outputs"
  | "review_quality";

export interface WorkflowNextAction {
  key: WorkflowActionKey;
  secondary: WorkflowActionKey[];
  /** True when the sentence is a statement; the UI must not draw a button. */
  informational: boolean;
}

export type WorkflowAttentionKey =
  | "reconstruction_failed"
  | "media_failed"
  | "quality_issues"
  | "unprepared_videos"
  | "coverage_insufficient";

export interface WorkflowAttentionItem {
  key: WorkflowAttentionKey;
  count: number | null;
}

export interface ProjectWorkflow {
  steps: WorkflowStep[];
  next_action: WorkflowNextAction | null;
  attention: WorkflowAttentionItem[];
  /** True only when a validated mesh artifact exists. Never from job status. */
  has_final_model: boolean;
  furthest_output: "sparse" | "dense" | "mesh" | "texture" | null;
  /*
   * Media counts, present only on the single-project workflow response. The
   * list already carries them at the row level and does not repeat them here.
   */
  media_count?: number | null;
  photo_count?: number | null;
  video_count?: number | null;
  failed_media_count?: number | null;
}

/** Account-wide totals. `model_count` is meshes, never succeeded jobs. */
export interface WorkspaceSummary {
  project_count: number;
  media_count: number;
  photo_count: number;
  video_count: number;
  sparse_project_count: number;
  model_count: number;
  reconstruction_job_count: number;
  active_job_count: number;
  attention_project_count: number;
}

export const TIMELINE_EVENT_KINDS = [
  "project_created",
  "capture_session_created",
  "capture_session_completed",
  "media_added",
  "analysis_completed",
  "coverage_completed",
  "videos_prepared",
  "reconstruction_queued",
  "reconstruction_succeeded",
  "reconstruction_failed",
] as const;
export type TimelineEventKind = (typeof TIMELINE_EVENT_KINDS)[number];

/** Derived from stored timestamps. No event table, and no stored sentence. */
export interface TimelineEvent {
  kind: TimelineEventKind;
  occurred_at: string;
  count: number | null;
  version: number | null;
  subject_id: string | null;
}

/**
 * One reconstruction run in the task centre.
 *
 * There is no `progress`, deliberately: the worker publishes a status, not a
 * percentage, and anything derived from elapsed time would animate through a
 * stage that had already stalled.
 */
export interface Task {
  job_id: string;
  project_id: string;
  project_name: string;
  version: number;
  status: ReconstructionStatus;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  failure_stage: string | null;
  /**
   * The phase the worker last reported, or null.
   *
   * Still not a progress figure. It is here so the task centre and the
   * reconstruction screen name the same phase: two places describing one run
   * from different data said different things about it.
   */
  current_phase?: ReconstructionPhase | null;
}

export interface SubjectPerceptionProfile {
  analysis_kind: "human_head" | "human_body" | "object" | "spatial";
  coverage_kind:
    | "human_head_semantic_v1"
    | "human_body_capture_v1"
    | "object_viewpoint_v1"
    | "spatial_viewpoint_v1";
  guidance_kind: "human_head" | "human_body" | "object" | "spatial";
  authoritative_coverage: boolean;
}

export const PROJECT_STATUSES = ["draft", "active", "archived"] as const;
export type ProjectStatus = (typeof PROJECT_STATUSES)[number];

export const CAPTURE_TYPES = ["photo", "video", "mixed"] as const;
export type CaptureType = (typeof CAPTURE_TYPES)[number];

export const CAPTURE_PURPOSES = ["initial", "correction"] as const;
export type CapturePurpose = (typeof CAPTURE_PURPOSES)[number];

export type CaptureSessionStatus = "pending" | "complete" | "failed";

export type MediaType = "photo" | "video";
export type MediaAssetStatus = "uploaded" | "probing" | "ready" | "failed";

/**
 * Four states, and the fourth is not a worse third.
 *
 * `unknown` means the region was never measurable - no frame ever showed it at
 * an angle the analysis could read. `insufficient` means it *was* measured and
 * the evidence fell short. Presenting them the same way tells the user their
 * capture failed when in fact nobody looked.
 */
export const COVERAGE_STATUSES = [
  "sufficient",
  "partial",
  "insufficient",
  "unknown",
] as const;
export type CoverageStatus = (typeof COVERAGE_STATUSES)[number];

/** The only five regions this system can measure. There is no "back of head". */
export const COVERAGE_REGIONS = [
  "front_face",
  "left_side",
  "right_side",
  "left_ear",
  "right_ear",
] as const;
export type CoverageRegion = (typeof COVERAGE_REGIONS)[number];

export type QualityIssueSeverity = "info" | "warning" | "critical";
export type QualityStatus = "excellent" | "good" | "usable" | "poor" | "rejected";

/** Generic media-quality result; it contains no face or subject semantics. */
/** One machine-readable reason a file was graded as it was. */
export interface QualityIssue {
  code: string;
  severity: QualityIssueSeverity;
  /** Backend prose, written for logs. Never rendered - see `i18n/issues.ts`. */
  explanation: string;
  measured_value: number | null;
  threshold: number | null;
}

export interface QualityAssessment {
  id: string;
  media_asset_id: string;
  version: number;
  scope: "media" | "frame";
  profile: string;
  analyzer: string;
  overall_score: number;
  sharpness_score: number;
  exposure_score: number;
  contrast_score: number;
  resolution_score: number;
  status: QualityStatus;
  issues?: QualityIssue[];
  recommendations: string[];
  created_at: string;
}

// --- resources -------------------------------------------------------------

export interface Project {
  id: string;
  name: string;
  description: string | null;
  subject_type: SubjectType;
  target_output: string | null;
  status: ProjectStatus;
  /**
   * Always present from the API. Optional in the type only so a fixture or a
   * cached response written before this field existed still type-checks;
   * readers treat a missing value as `precise`, which is what the backend
   * stores for every project that never chose.
   */
  geometry_mode?: GeometryMode;
  created_at: string;
  updated_at: string;
  /** Backend-derived from the stored subject type; never supplied on writes. */
  capabilities?: SubjectCapabilities;
  /** Versioned perception policy selected by the backend. */
  perception_profile?: SubjectPerceptionProfile;
}

/**
 * A project as the library lists it.
 *
 * Extends `Project` because it is the same record with more known about it.
 * The four extra fields are what the library filters and sorts on, and they
 * arrive with the list so the screen never issues one request per project.
 *
 * `last_activity_at` is not `updated_at`. Nothing in the backend writes a
 * project row after creation, so `updated_at` equals `created_at` forever;
 * real activity is the newest capture, file or reconstruction, which is what
 * this field carries.
 *
 * Every nullable field here is `null` for "never happened", never a zero or a
 * failing status. A project nobody analysed and a project whose analysis came
 * out badly lead the user to different actions, so the UI must be able to tell
 * them apart.
 */
export interface ProjectListItem extends Project {
  capture_session_count: number;
  media_count: number;
  last_activity_at: string;
  latest_coverage_status: CoverageStatus | null;
  measured_region_count: number | null;
  latest_reconstruction_status: ReconstructionStatus | null;

  /*
   * Workflow facts, carried by the list so a fifty-project library still
   * costs one request. Optional in the type because an older API answers
   * without them and a card that assumes them would render `undefined`.
   */
  photo_count?: number;
  video_count?: number;
  failed_media_count?: number;
  quality_assessed_count?: number;
  quality_issue_count?: number;
  prepared_video_count?: number;
  /**
   * The project's first ready photograph, for the card's thumbnail.
   *
   * An id rather than a URL: the image is fetched from
   * `/media/{id}/preview`, the same authenticated, ownership-checked route
   * every other preview in the product uses. Optional because an older API
   * answers without it, and absent for a project that has no photo yet.
   */
  cover_media_id?: string | null;
  workflow?: ProjectWorkflow | null;
}

export interface UpdateProjectRequest {
  name?: string;
  description?: string | null;
  /**
   * Changes what the *next* reconstruction may do. It re-describes nothing
   * that already exists: every job carries the mode it ran under, so turning
   * this back to `precise` does not convert completed geometry into observed
   * geometry. Omit the key to leave it alone.
   */
  geometry_mode?: GeometryMode;
}

/** How far a format gets through the pipeline. Storable is not usable. */
export type FormatSupportLevel = "import" | "preview" | "analyze" | "reconstruct";

export interface FormatCapability {
  label: string;
  media_type: MediaType;
  extensions: string[];
  support: FormatSupportLevel;
  note: string;
}

/**
 * What the server actually accepts.
 *
 * Fetched rather than hardcoded. The uploader's `accept` attribute, the
 * import dialog's format panel and the help centre's format section all read
 * this one response, so none of them can promise a format the backend refuses
 * - which is exactly what three hand-maintained lists eventually do.
 */
export interface MediaCapabilities {
  formats: FormatCapability[];
}

/**
 * What an AI capability needs before it can be offered, and what it lacks.
 *
 * `available` decides whether a control does anything; `blockers` decides what
 * to say when it does not. They are separate because "there is no such model
 * anywhere", "the licence has not been reviewed" and "this machine has no GPU"
 * are three different facts for three different people, and one word for all
 * three sends everybody the wrong way.
 */
export type ModelTaskName =
  | "geometry_completion"
  | "image_deblur"
  | "image_super_resolution"
  | "region_estimation";

export interface ModelTaskCapability {
  task: ModelTaskName;
  available: boolean;
  /** Subject types a usable model claims. Never assumed to generalise. */
  subjects: SubjectType[];
  /**
   * `no_model`, `weights_absent`, `weights_missing`, `licence_unreviewed`,
   * `licence_refused`, `hardware_cuda_absent`, `disabled`. Empty when the task
   * is available.
   */
  blockers: string[];
  /** Every catalogued model for this task and its recorded state. */
  models: Record<string, unknown>[];
  /**
   * For super resolution, the factors this installation can actually perform.
   * What is on disk, not what the architecture supports: offering 4x beside a
   * machine holding only the 2x file advertises something that is not there.
   * Empty for every other task.
   */
  scales?: number[];
  /**
   * Whether this task's output may be believed as a measurement. `false` for
   * super resolution - Real-ESRGAN produces plausible detail rather than
   * recovering photographed detail - and the UI must say so beside the button.
   * Null where the question does not apply.
   */
  recovers_real_detail?: boolean | null;
}

export interface ModelCapabilities {
  /** Probed, never assumed. "Not here" is not "not possible". */
  cuda_available: boolean;
  tasks: ModelTaskCapability[];
}

export interface CreateProjectRequest {
  name: string;
  subject_type: SubjectType;
  description?: string | null;
  target_output?: string | null;
  status?: ProjectStatus;
  /** Omitted means `precise`, which is what the backend defaults to. */
  geometry_mode?: GeometryMode;
}

export interface CaptureSession {
  id: string;
  project_id: string;
  capture_type: CaptureType;
  purpose: CapturePurpose;
  status: CaptureSessionStatus;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateCaptureSessionRequest {
  capture_type: CaptureType;
  purpose?: CapturePurpose;
  notes?: string | null;
}

/**
 * Media metadata. Note what is absent: there is no URL, and no
 * `stored_filename`. The API serves no bytes, so the UI has no image to show
 * and must not construct a path to one.
 */
export interface MediaAsset {
  id: string;
  capture_session_id: string;
  media_type: MediaType;
  status: MediaAssetStatus;
  original_filename: string;
  mime_type: string;
  file_extension: string;
  size_bytes: number;
  sha256: string;
  width?: number | null;
  height?: number | null;
  duration_seconds?: number | null;
  fps?: number | null;
  frame_count?: number | null;
  codec_name?: string | null;
  image_format?: string | null;
  guided_coverage_region?: string | null;
  /**
   * How the region was decided, and whether anybody still has to look at it.
   *
   * The API has published these since regions existed; this type did not carry
   * them, so the interface could not tell a model's suggestion from a person's
   * choice and had no way to show that an estimate had been made at all. That
   * is what "bölge atama olmamış gibi duruyor" looked like from the outside.
   */
  guided_region_source?:
    | "unassigned"
    | "vision_model"
    | "pose_assisted"
    | "capture_confirmed"
    | "manual";
  guided_region_review_state?:
    | "settled"
    | "tentative"
    | "auto_assigned"
    | "review_required"
    | "undetermined";
  /** Calibrated, never a raw score. Null for anything a machine did not decide. */
  guided_region_confidence?: number | null;
  /** The runner-up, for the two-choice review assist. */
  guided_region_alternate?: string | null;
  guided_region_alternate_confidence?: number | null;
  created_at: string;
  updated_at: string;
}

export interface MediaCountSummary {
  total: number;
  photos: number;
  videos: number;
  /** Photographs whose capture angle a person still has to settle. */
  region_review_required?: number;
}

/**
 * The whole session's verdicts, counted by the server.
 *
 * Separate from `MediaCountSummary` for the same reason the endpoints are
 * separate: that one counts what was uploaded, this one joins the newest
 * assessment per file. `statuses` omits a grade nothing holds rather than
 * sending a zero, so read it with a default.
 */
export interface SessionQualitySummary {
  total: number;
  assessed: number;
  unassessed: number;
  statuses: Partial<Record<QualityStatus, number>>;
}

export interface MediaEnhancement {
  id: string;
  source_media_asset_id: string;
  status: "beneficial" | "review_required";
  pipeline_version: string;
  transformations: string[];
  parameters: Record<string, unknown>;
  before_metrics: Record<string, number>;
  after_metrics: Record<string, number>;
  original_width: number;
  original_height: number;
  output_width: number;
  output_height: number;
  output_format: string;
  size_bytes: number;
  sha256: string;
  beneficial: boolean;
  reconstruction_allowed: false;
  created_at: string;
}

// --- coverage --------------------------------------------------------------

/**
 * `score` is **coverage** - how much of this region the capture covered. It is
 * not an accuracy, not a confidence, and not a probability that anything is
 * correct. `confidence` is a separate number saying how trustworthy the
 * coverage figure itself is. Conflating them is the single most misleading
 * thing this UI could do, so they carry different Turkish labels everywhere:
 * Kapsama and Güven.
 */
export interface ProjectOverallCoverage {
  status: CoverageStatus;
  score: number;
  confidence: number;
  /** Regions the analysis could actually evaluate. Never assume all five. */
  measured_regions?: CoverageRegion[] | null;
  /** Free-text areas the system does not measure at all. Show them verbatim. */
  unmeasured_areas?: string[] | null;
}

export interface ProjectRegionCoverage {
  status: CoverageStatus;
  coverage_score: number;
  coverage_confidence: number;
  /** Distinct viewpoints, not frame count. Ten frames from one angle is one. */
  independent_views: number;
  source_session_count: number;
  best_session_score?: number | null;
  conflicting_session_count?: number | null;
  /** Present when status is `unknown`: why it could not be measured. */
  unmeasurable_reason?: string | null;
}

export interface ProjectRegionChange {
  before_status: CoverageStatus;
  after_status: CoverageStatus;
  before_score: number;
  after_score: number;
  score_delta: number;
  status_changed: boolean;
  improved: boolean;
}

export interface ProjectCoverageSource {
  capture_session_id: string;
  purpose: CapturePurpose;
  head_coverage_analysis_id: string;
  head_coverage_version: number;
}

export interface ProjectSessionSummary {
  total_sessions: number;
  analyzed_sessions: number;
  unanalyzed_sessions: number;
}

export interface ProjectCoverageIssue {
  code: string;
  severity: QualityIssueSeverity;
  region?: CoverageRegion | null;
  capture_session_id?: string | null;
  /** Backend-authored prose. Rendered as text, never as markup. */
  explanation: string;
  measured_value?: number | null;
  threshold?: number | null;
}

export interface ProjectCoverage {
  id: string;
  project_id: string;
  version: number;
  coverage_profile_name: string;
  coverage_profile_version: number;
  overall: ProjectOverallCoverage;
  regions?: Partial<Record<CoverageRegion, ProjectRegionCoverage>> | null;
  sources?: ProjectCoverageSource[] | null;
  session_summary: ProjectSessionSummary;
  changes?: Partial<Record<CoverageRegion, ProjectRegionChange>> | null;
  issues?: ProjectCoverageIssue[] | null;
  created_at: string;
}

// --- per-session analyses --------------------------------------------------
// These are consumed only as "did it succeed", so they are typed loosely on
// purpose: guessing at fields the screens never read would be inventing
// contract. `version` is the one thing the UI does use, to chain the next step.

export interface AnalysisResult {
  id: string;
  version: number;
  [key: string]: unknown;
}

/** One entry in the project coverage history. `GET /projects/{id}/coverages`. */
export interface ProjectCoverageSummary {
  id: string;
  version: number;
  coverage_profile_name: string;
  coverage_profile_version: number;
  overall_status: CoverageStatus;
  overall_coverage_score: number;
  included_capture_session_count: number;
  created_at: string;
}

export interface ViewpointCoverage {
  project_id: string;
  coverage_kind: "human_body_capture_v1" | "object_viewpoint_v1" | "spatial_viewpoint_v1";
  status: "evaluated" | "not_evaluated" | "insufficient_evidence" | "not_applicable";
  reason: string | null;
  algorithm: string;
  algorithm_version: number;
  source_reconstruction_job_id: string | null;
  source_reconstruction_version: number | null;
  source_artifact_id: string | null;
  source_artifact_sha256: string | null;
  evidence: null | {
    registered_camera_count: number;
    viewpoint_cluster_count: number;
    connected_camera_count: number;
    angular_spread_degrees: number;
    points: Array<{ key: number; x: number; y: number }>;
    links: Array<{ source: number; target: number }>;
  };
}

/**
 * One capture angle's state.
 *
 * `missing` / `problematic` / `weak` / `unverified` / `sufficient`, decided on
 * the server so that the screen cannot reach a different verdict than the
 * next-action did. `sufficient` means *enough images to work from*, never
 * "this angle is finished" - the threshold behind it is the engine's
 * geometric floor and the profile that defines it says in as many words that
 * it is not a product quality threshold.
 */
export type GuidedRegionStatus =
  | "missing"
  | "problematic"
  | "weak"
  | "unverified"
  | "sufficient";

export interface GuidedRegionEvidence {
  region: string;
  total_media: number;
  /** With `problematic_media` and `unassessed_media`, partitions the total. */
  usable_media: number;
  problematic_media: number;
  unassessed_media: number;
  /**
   * Files carrying at least one finding. **Overlaps the partition above on
   * purpose** - a file the grader still accepted can carry a minor finding -
   * so this is never added to the three counts.
   */
  quality_issue_media: number;
  /** Findings, not files: one file can carry several. */
  issue_counts: Record<string, number>;
  status: GuidedRegionStatus;
  /** Lower sorts first. The screen does not have to know the ranking rule. */
  priority: number;
  /** A person or the guided capture flow decided these. */
  confirmed_media: number;
  /** A model suggested these, and anything human outranks it. */
  estimated_media: number;
}

/** What the server says to do next. A code plus the numbers its sentence needs. */
export type NextActionKind =
  | "start_capturing"
  | "capture_region"
  | "recapture_region"
  | "strengthen_region"
  | "assign_unmarked"
  | "review_uncertain"
  | "run_analysis"
  | "nothing_to_do";

export interface GuidedNextAction {
  kind: NextActionKind;
  region: string | null;
  /** How many more usable files are being asked for. A real subtraction. */
  shortfall: number;
  media_count: number;
}

/** One photograph in the review queue, with what the model narrowed it to. */
export interface RegionReviewItem {
  media: MediaAsset;
  /**
   * The stations the estimator shortlisted, best first, or empty when it had
   * nothing usable to say. Two buttons instead of twelve is the whole of what
   * a model below the release gate is allowed to contribute.
   */
  candidates: string[];
}

export interface RegionReview {
  subject_type: SubjectType;
  /** This subject's stations, in capture order, from the server's ontology. */
  regions: string[];
  items: RegionReviewItem[];
  /** Across the whole project, not just this page. */
  remaining: number;
  settled: number;
  /**
   * Whether this subject's benchmark has cleared the release gate. False
   * everywhere today, which is why the screen says the model proposes.
   */
  auto_assignment_enabled: boolean;
}

/**
 * One person's two coverage truths, never merged.
 *
 * `person` is ordinal and scoped to the project - "the first person this
 * capture noticed". It is not an identity and carries no claim about who
 * anybody is.
 */
export interface ScenePerson {
  person: string;
  /** Head region to coverage status. `unknown` where nothing judged it. */
  face: Record<string, CoverageStatus>;
  /** Body part to visibility. All `not_assessed` while nothing measures a body. */
  body: Record<string, string>;
  frame_count: number;
  /** Whether this person moved, as opposed to the camera moving around them. */
  moved: boolean;
}

export interface SceneAction {
  action: string;
  person: string | null;
  target: string | null;
  /** Turkish, one sentence, from the server so two screens cannot word it
   * differently. A `photograph_place_region` row is the exception: its
   * sentence carries the raw station id, because the station labels are a
   * frontend map and a second copy in Python is two literals that drift.
   * `actionSentence` in `SceneCoveragePanel` relabels that one case and
   * falls back to this string. */
  message: string;
  priority: number;
}

/**
 * A place and the people in it.
 *
 * **There is no overall percentage and there is no field to compute one into.**
 * A scene where the room is complete and one person was photographed only from
 * behind would score high and be useless, which is the whole reason this
 * subject type exists.
 */
export interface SceneCoverage {
  place: Record<string, CoverageStatus>;
  people: ScenePerson[];
  actions: SceneAction[];
  /** A range or a number, in the customer's words. Null before any analysis. */
  people_summary: string | null;
  people_floor: number;
  people_ceiling: number;
  people_certain: boolean;
  people_count_uncertain: boolean;
  /** Zero means nothing has looked, which is not the same as an empty room. */
  analysed_frames: number;
  /** False means the count is a floor and no tracking was attempted. */
  frames_ordered: boolean;
  /** False in this build. Published so the screen states it. */
  body_coverage_measured: boolean;
}

export interface GuidedCoverage {
  project_id: string;
  subject_type: Exclude<SubjectType, "human_head">;
  semantics: "user_guided_capture_intent";
  regions: GuidedRegionEvidence[];
  /** Files nobody ever marked with an angle. */
  unassigned_media: number;
  /** Files something looked at and would not commit to. A different fix. */
  review_required_media: number;
  next_action: GuidedNextAction;
  sufficiency_threshold: number;
  sufficiency_basis: string;
}

/**
 * The outcome of choosing frames from one video.
 *
 * `selected_count` can legitimately be zero - every candidate frame may be
 * rejected as too blurry - and that is a state the UI has to report honestly,
 * because the analysis that follows will then refuse to run.
 */
export interface KeyframeSelection {
  id: string;
  media_asset_id: string;
  version: number;
  selector: string;
  candidate_count: number;
  selected_count: number;
  rejected_count: number;
  rejection_breakdown?: Record<string, number> | null;
  created_at: string;
}

/** One notification. `kind` is a code; the sentence is chosen when rendered. */
export interface Notification {
  id: string;
  kind: string;
  project_id?: string | null;
  created_at: string;
  read_at?: string | null;
  /** Allow-listed safe values only - never a path, a traceback or a key. */
  metadata?: Record<string, string>;
}

export interface Health {
  status?: string | null;
  service: string;
  version: string;
  /** Bumped with every deployed change. Optional: an older API omits it. */
  build?: string | null;
  /** When the running process started. ISO 8601. */
  started_at?: string | null;
}

/**
 * The only error shape the API produces. `detail` is written for a developer:
 * it is logged, and shown to the user only where §ui.errors permits, never as
 * the primary message on a screen.
 */
export interface ApiErrorBody {
  detail: string;
}

// --- phone pairing ----------------------------------------------------------

/** Server-authoritative. The UI never derives a pairing state of its own. */
export type PairingStatus = "waiting" | "connected" | "expired" | "revoked";

/**
 * The one response that carries a credential: `pairing_url` has the one-time
 * secret in its fragment. It is turned straight into a QR and is never stored,
 * logged, or shown as text.
 */
export interface PairingCreated {
  id: string;
  status: PairingStatus;
  expires_at: string;
  pairing_url: string;
  project_name: string;
}

/** What polling returns. Deliberately has no field that could hold a secret. */
export interface PairingState {
  id: string;
  status: PairingStatus;
  expires_at: string;
  connected_at?: string | null;
  device_label?: string | null;
}

export interface PairingAvailability {
  enabled: boolean;
  configured: boolean;
  reason?: string | null;
}

// --- reconstruction ---------------------------------------------------------

export const RECONSTRUCTION_STATUSES = [
  "queued",
  "running",
  "succeeded",
  "failed",
  "cancelled",
] as const;
export type ReconstructionStatus = (typeof RECONSTRUCTION_STATUSES)[number];

/**
 * Only the kinds the pipeline can actually emit today. `mesh` and
 * `textured_mesh` are absent from the backend enum because COLMAP's dense
 * stage is CUDA-only and the available build has none - so the UI has no
 * branch that could render a mesh it was handed by mistake.
 *
 * The dense and mesh kinds now exist, and their presence in this union is not
 * a claim that this deployment can produce them. `ReconstructionCapability`
 * answers that, from the real engine binary. What the union does is stop the
 * UI casting strings to talk about artifacts the backend can already name.
 */
export type ReconstructionArtifactKind =
  | "camera_poses"
  | "sparse_point_cloud"
  | "dense_point_cloud"
  | "mesh"
  | "textured_mesh"
  | "model_glb"
  | "engine_log";

/** How far one output of the geometry pipeline got. */
export type StageState = "not_started" | "running" | "ready" | "failed" | "unavailable";

export interface StageOutcome {
  state: StageState;
  points: number | null;
  vertices: number | null;
  faces: number | null;
  artifact_id: string | null;
  byte_size: number | null;
}

/**
 * What a project actually has, stage by stage.
 *
 * Derived on the server from validated artifacts. `has_model` is the **only**
 * thing that licenses "3D Model hazır", and it is never computed from a job
 * status - a sparse-only job succeeds perfectly well and contains no surface.
 *
 * `mesh` and `model` are separate stages, and the difference is the one this
 * screen keeps getting wrong. A mesh is a surface; a model is that surface
 * packaged into a GLB a browser can open. The pipeline can produce the first
 * and fail at the second, so `has_model` reads `model`, not `mesh` - showing a
 * viewer on `mesh.state === "ready"` opens it with no file to load.
 *
 * `unavailable` and `failed` are different words: the first means this
 * deployment cannot perform the stage at all, the second that it tried and did
 * not manage. Telling someone to reshoot because a server has no GPU would
 * waste their afternoon.
 */
export interface ModelState {
  sparse: StageOutcome;
  dense: StageOutcome;
  mesh: StageOutcome;
  texture: StageOutcome;
  /** The packaged, deliverable GLB. Distinct from `mesh` - see above. */
  model: StageOutcome;
  has_model: boolean;
  furthest_ready: "sparse" | "dense" | "mesh" | "model" | "texture" | null;
}

/** Which stages this installation can attempt. Not a promise about results. */
/**
 * One piece of work still running in a project.
 *
 * `kind` is a stable key, not a sentence: the backend stores nothing
 * user-facing in a language, so the words come from `i18n/terms`.
 */
export interface ActiveWork {
  kind: string;
  id: string;
  version: number;
  status: string;
  phase: string | null;
}

export interface ReconstructionCapability {
  sparse: boolean;
  dense: boolean;
  mesh: boolean;
  texture: boolean;
  /**
   * Whether a machine that runs reconstructions is reporting in.
   *
   * Separate from the stage flags, and not derivable from them: a connected
   * worker on a CPU build has `dense: false` and is very much online.
   * Optional because responses written before the field existed do not carry
   * it, and `undefined` must not be read as `false`.
   */
  worker_online?: boolean;
  engine: string;
  engine_version: string;
  /**
   * Every provider this deployment knows, and whether it may be used.
   *
   * Optional for the same reason `worker_online` is: a response from an older
   * server does not carry it, and an empty list must not be read as "no
   * providers exist" - it means "this server does not say".
   */
  providers?: ProviderStatus[];
}

/**
 * One provider, as the server reports it.
 *
 * Deliberately carries no credential, no key fingerprint and no endpoint.
 * Whether a key is configured is not even here: what a screen needs is whether
 * the provider is usable and why not, and everything finer than that is an
 * operator's business rather than a browser's.
 */
export interface ProviderStatus {
  id: string;
  /** photogrammetry measures, import brings in, generative invents. */
  kind: "photogrammetry" | "import" | "generative" | string;
  availability: "enabled" | "ready_but_disabled" | "unimplemented" | string;
  /** Why it is unavailable, for an operator. Empty when it is available. */
  reason?: string;
  /** Whether choosing it sends customer data to a machine we do not run. */
  sends_data_off_device?: boolean;
  /** Whether a verified measurement may rest on this provider's output. */
  may_be_measured?: boolean;
}

export interface ReconstructionArtifact {
  id: string;
  artifact_kind: ReconstructionArtifactKind;
  artifact_format: string;
  byte_size: number;
  created_at: string;
}

/**
 * A video whose frames have not been chosen for reconstruction yet.
 *
 * Identified by what its owner would recognise. The backend deliberately sends
 * no storage key and no selector name, so there is nothing here that could
 * leak onto a screen as jargon.
 */
export interface UnpreparedVideo {
  media_asset_id: string;
  capture_session_id: string;
  original_filename: string;
  duration_seconds?: number | null;
}

/**
 * What stands between a project and a reconstruction.
 *
 * `can_start` and `needs_preparation` are independent: a project can hold
 * enough photographs to solve right now *and* an unprepared video whose frames
 * would otherwise silently count for nothing.
 */
/**
 * One thing standing between a project and a run.
 *
 * A code and its numbers. The backend deliberately sends no sentence: the
 * wording belongs to the screen, so a copy edit in Python cannot change what a
 * person is told without anybody reviewing it as copy.
 */
export interface PreflightFinding {
  code: string;
  /**
   * `blocker` means the create call will refuse, so the start button must not
   * be offered. `warning` means it will run and the reader should know
   * something - never a gate.
   */
  severity: "blocker" | "warning";
  count: number | null;
  required: number | null;
}

export interface ReconstructionReadiness {
  can_start: boolean;
  needs_preparation: boolean;
  /**
   * Whether the API process can see an engine. A hint, never provenance: the
   * worker is a different container and only its answer describes the machine
   * that runs the job.
   */
  api_engine_available?: boolean;
  photo_count: number;
  prepared_video_count: number;
  usable_frame_count: number;
  minimum_required: number;
  maximum_allowed: number;
  unprepared_videos: UnpreparedVideo[];
  /**
   * Blockers first, then warnings. `can_start` above is exactly "no blockers
   * here" - the screen must not re-derive it, because a second derivation is
   * how a button starts disagreeing with the endpoint behind it.
   *
   * Optional so a server that predates the preflight degrades to the counts.
   */
  findings?: PreflightFinding[];
}

export interface ReconstructionMetrics {
  input_image_count: number;
  processed_image_count: number;
  registered_camera_count: number;
  /**
   * Photographs that got a camera in any model, not only the delivered one.
   *
   * Optional because a job recorded before this was measured does not carry
   * it, and `undefined` must not be read as zero - that would assert nothing
   * solved on a reconstruction that worked.
   */
  solved_camera_count?: number | null;
  /** How many models the mapper wrote. More than one is a split capture. */
  model_count?: number | null;
  registration_ratio: number;
  sparse_point_count: number;
  track_count?: number | null;
  mean_track_length?: number | null;
  mean_reprojection_error?: number | null;
  working_resolution?: number | null;
  stage_seconds?: Record<string, number>;
  total_seconds?: number | null;
}

/**
 * How a job's input set was bounded.
 *
 * Absent on jobs created before this was recorded, and absent means *unknown* -
 * never "nothing was dropped". `sampled` is a supported outcome, not a fault.
 */
export interface InputSampling {
  strategy: string;
  sampled: boolean;
  resolved_input_count: number;
  selected_input_count: number;
  omitted_input_count: number;
  budget: number;
}

export interface ReconstructionJob {
  id: string;
  project_id: string;
  version: number;
  status: ReconstructionStatus;
  /** Read this, never `status`, to decide what geometry exists. */
  model_state: ModelState;
  engine_name: string;
  /**
   * Stamped by the process that executed the job. `undetermined` while queued,
   * `unknown` when the worker looked and its binary would not say. Older jobs
   * may carry `unavailable`, which was the API process describing its own
   * machine rather than the one that solved them.
   */
  engine_version: string;
  /** `nocuda` / `cuda`, or absent when unrecorded or unstated. */
  engine_capabilities?: string | null;
  profile_name: string;
  input_image_count: number;
  queued_at?: string | null;
  started_at?: string | null;
  finished_at?: string | null;
  failure_code?: string | null;
  failure_stage?: string | null;
  failure_summary?: string | null;
  /**
   * Coarse phase the executing worker last reported.
   *
   * Advisory, and null on a queued job or one run before phases were recorded.
   * What geometry exists is still `model_state`; this only says where a
   * running job has got to, which is the difference between twenty minutes of
   * "İşleniyor" and twenty minutes somebody can follow.
   */
  current_phase?: ReconstructionPhase | null;
  metrics?: ReconstructionMetrics | null;
  artifacts: ReconstructionArtifact[];
  sampling?: InputSampling | null;
  created_at: string;
}

/**
 * The pipeline as a person waiting for a model would describe it.
 *
 * Distinct from the engine's own stages: `feature_extraction` and
 * `feature_matching` are both `sparse` here, because the difference matters to
 * a profiler and not to somebody watching a progress line. Transfer appears
 * because a remote worker spends real minutes moving bytes, and a screen that
 * said "sparse" through it would be lying about where the time went.
 */
export type ReconstructionPhase =
  | "input_transfer"
  | "sparse"
  | "dense"
  | "mesh"
  | "texture"
  | "glb"
  | "output_transfer";

export interface ReconstructionJobSummary {
  id: string;
  version: number;
  status: ReconstructionStatus;
  engine_name: string;
  profile_name: string;
  registered_camera_count: number;
  sparse_point_count: number;
  created_at: string;
}

// --- accounts ---------------------------------------------------------------

/**
 * The signed-in user. Deliberately has no token field: the session lives in an
 * HttpOnly cookie the browser sends on its own, and JavaScript never sees it.
 */
export interface SessionUser {
  id: string;
  email: string;
  display_name: string;
}

/**
 * One live session, as the settings screen may show it.
 *
 * Note what the server does not send: the token, its digest, an IP address or
 * a raw User-Agent. `device_label` is a coarse browser family - "Chrome",
 * "Safari" - which is enough to recognise a row and useless as a fingerprint.
 */
export interface AccountSession {
  id: string;
  device_label: string | null;
  created_at: string;
  expires_at: string;
  current: boolean;
}


/**
 * One row on the outputs screen: a format somebody can actually download.
 *
 * Not the same list as artifact kinds. An artifact kind is what a pipeline
 * stage wrote; a delivery format is what a person opens - which is why STL
 * and the OBJ package appear here and are not artifacts, being derived from
 * the stored model on request.
 */
export interface FormatOffer {
  format: "glb" | "obj" | "stl" | "3mf" | "usdz" | "ply" | "colmap";
  label: string;
  description: string;
  recommended: boolean;
  /**
   * Whether the file carries image-derived appearance. `false` on STL is a
   * fact about the format, not a defect in the model, and the screen says so.
   */
  carries_texture: boolean;
  /** Built on request rather than stored, so the screen can say it takes a moment. */
  derived: boolean;
  /**
   * The stored file's size, or null for a format built on request.
   *
   * Null is not zero and not an estimate: an STL of the same mesh differs from
   * its GLB by a factor nobody can predict from a vertex count, and a number
   * that turned out wrong by half is worse than none for somebody deciding
   * what to put on a phone.
   */
  byte_size?: number | null;
}


/**
 * A reconstruction's real-world scale, or the absence of one.
 *
 * `verified: false` is a real state, not an error: photogrammetry produces
 * geometry that is correct in shape and arbitrary in size, and until somebody
 * supplies one known distance the product must not print a millimetre figure
 * anywhere.
 */
export interface ScaleCalibration {
  reconstruction_job_id: string;
  verified: boolean;
  source: "manual_reference" | "lidar" | "known_marker" | null;
  /** Multiply a model-space length by this to get metres. */
  scale_factor: number | null;
  reference_length: number | null;
  reference_unit: string | null;
  model_distance: number | null;
  /** Decimals a measurement may honestly be shown to, from the reference. */
  display_decimals: number | null;
  calibrated_at: string | null;
}


/**
 * The delivered model's own condition, measured from the model.
 *
 * Deliberately carries no scale. Scale answers "how big is this"; this answers
 * "is this a solid a slicer can use", and the two fail independently - a
 * calibrated model with a hole prints as a shell full of air. Two rows on the
 * screen, never one.
 */
export interface MeshHealth {
  readiness: "ready" | "needs_repair" | "not_ready";
  issues: string[];
  /** One sentence per issue, from the server, so no two screens word it differently. */
  messages: string[];
  watertight: boolean;
  manifold: boolean;
  consistently_oriented: boolean;
  shell_count: number;
  boundary_loop_count: number;
  degenerate_face_count: number;
  /** Sampled, never exhaustive. Read only alongside its sample size. */
  self_intersection_estimate: number;
  self_intersection_sample: number;
  /** Always false: a real print constraint that cannot be measured honestly yet. */
  wall_thickness_measured: boolean;
  /**
   * What the project asked for when this model was built. Read from the job,
   * so a mode changed afterwards does not re-describe what the run produced.
   * `null` on jobs that predate the record.
   */
  geometry_mode?: GeometryMode | null;
  /** Whether any of this surface was created rather than photographed. */
  contains_ai_completed?: boolean;
  /**
   * Whether the artifact records where its surface came from at all. False on
   * every model made before that was measured - a third state, not a "no":
   * the pipeline did not look, which is not the same as having looked and
   * found none.
   */
  provenance_recorded?: boolean;
  vertex_count: number;
  face_count: number;
  /** Longest side first, in the model's own units. Not millimetres. */
  dimensions: number[];
}
